import React, { useEffect, useRef, useState } from "react";

/** "Eco-Allies: Battle for NYC" — tap e-waste minions, charge the Eco-Beam, defeat the Trash-Bot. */

const W = 400;
const H = 560;
const CITY_LINE = H - 46;

const MINIONS = ["🔌", "🔋", "📺", "🗑️", "📱", "💻", "⚙️", "📟"];
const SIDEKICKS = ["♻️", "💧", "🌬️", "☀️", "🌳"];

type Minion = { x: number; y: number; vy: number; r: number; emoji: string };
type Particle = { x: number; y: number; vx: number; vy: number; life: number; color: string; size: number };
type Phase = "ready" | "playing" | "won" | "lost";

const PUBLIC = process.env.PUBLIC_URL || "";

const EcoBattleGame: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [phase, setPhase] = useState<Phase>("ready");
  const [finalScore, setFinalScore] = useState(0);
  const bgRef = useRef<HTMLImageElement | null>(null);
  const bgLoaded = useRef(false);

  const g = useRef({
    running: false,
    minions: [] as Minion[],
    parts: [] as Particle[],
    bossHP: 100,
    shield: 3,
    score: 0,
    eco: 0,
    elapsed: 0,
    spawnTimer: 0,
    spawnEvery: 70,
    bossFlash: 0,
    beam: 0,
    shake: 0,
    hitFlash: 0,
  });

  // preload background
  useEffect(() => {
    const img = new Image();
    img.onload = () => { bgLoaded.current = true; };
    img.src = `${PUBLIC}/battle-bg.png`;
    bgRef.current = img;
  }, []);

  const start = () => {
    const s = g.current;
    s.minions = []; s.parts = [];
    s.bossHP = 100; s.shield = 3; s.score = 0; s.eco = 0;
    s.elapsed = 0; s.spawnTimer = 0; s.spawnEvery = 70;
    s.bossFlash = 0; s.beam = 0; s.shake = 0; s.hitFlash = 0;
    s.running = true;
    setPhase("playing");
  };

  const burst = (x: number, y: number, color: string, n = 12) => {
    const s = g.current;
    for (let i = 0; i < n; i++) {
      const a = Math.random() * Math.PI * 2;
      const sp = 1 + Math.random() * 3.5;
      s.parts.push({ x, y, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp, life: 30, color, size: 2 + Math.random() * 3 });
    }
  };

  const fireBeam = () => {
    const s = g.current;
    s.eco = 0; s.beam = 22; s.bossFlash = 22; s.shake = 12;
    s.bossHP = Math.max(0, s.bossHP - 18);
    s.minions.forEach((m) => burst(m.x, m.y, "#22e58f", 8));
    s.minions = [];
  };

  // tap to zap
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const onTap = (e: PointerEvent) => {
      const s = g.current;
      if (!s.running) return;
      const r = canvas.getBoundingClientRect();
      const x = ((e.clientX - r.left) / r.width) * W;
      const y = ((e.clientY - r.top) / r.height) * H;
      let hit = -1, hd = 1e9;
      for (let i = 0; i < s.minions.length; i++) {
        const m = s.minions[i];
        const d = Math.hypot(m.x - x, m.y - y);
        if (d < m.r + 16 && d < hd) { hd = d; hit = i; }
      }
      if (hit >= 0) {
        const m = s.minions[hit];
        burst(m.x, m.y, "#3ea6ff", 14);
        s.minions.splice(hit, 1);
        s.score += 10;
        s.eco = Math.min(100, s.eco + 12);
        if (s.eco >= 100) fireBeam();
      }
    };
    canvas.addEventListener("pointerdown", onTap);
    return () => canvas.removeEventListener("pointerdown", onTap);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // game loop
  useEffect(() => {
    if (phase !== "playing") return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d")!;
    const game = g.current;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = W * dpr; canvas.height = H * dpr;
    ctx.scale(dpr, dpr);
    let raf = 0;

    const loop = () => {
      const s = g.current;
      if (!s.running) return;
      s.elapsed++;
      if (s.spawnEvery > 32 && s.elapsed % 180 === 0) s.spawnEvery -= 4;

      s.spawnTimer++;
      if (s.spawnTimer >= s.spawnEvery) {
        s.spawnTimer = 0;
        s.minions.push({
          x: 30 + Math.random() * (W - 60),
          y: -16,
          vy: 1.3 + Math.random() * 1.0 + s.elapsed / 3200,
          r: 18,
          emoji: MINIONS[(Math.random() * MINIONS.length) | 0],
        });
      }

      for (let i = s.minions.length - 1; i >= 0; i--) {
        const m = s.minions[i];
        m.y += m.vy;
        if (m.y >= CITY_LINE) {
          s.minions.splice(i, 1);
          s.shield -= 1; s.shake = 10; s.hitFlash = 14;
          burst(m.x, CITY_LINE, "#e23b3b", 10);
        }
      }

      for (let i = s.parts.length - 1; i >= 0; i--) {
        const p = s.parts[i];
        p.x += p.vx; p.y += p.vy; p.vy += 0.08; p.life--;
        if (p.life <= 0) s.parts.splice(i, 1);
      }

      if (s.bossFlash > 0) s.bossFlash--;
      if (s.beam > 0) s.beam--;
      if (s.shake > 0) s.shake--;
      if (s.hitFlash > 0) s.hitFlash--;

      if (s.bossHP <= 0) { s.running = false; setFinalScore(s.score); setPhase("won"); }
      else if (s.shield <= 0) { s.running = false; setFinalScore(s.score); setPhase("lost"); }

      draw(ctx, s, bgRef.current, bgLoaded.current);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); game.running = false; };
  }, [phase]);

  return (
    <div className="tbg-wrap">
      <div className="tbg-stage">
        <canvas ref={canvasRef} className="tbg-canvas" style={{ aspectRatio: `${W} / ${H}` }} />

        {phase === "ready" && (
          <div className="tbg-overlay">
            <img className="eb-cover" src={`${PUBLIC}/battle-cover.png`} alt="Eco-Allies battle for NYC" />
            <div className="tbg-kicker">★ Eco-Allies Arcade ★</div>
            <h3 className="tbg-title">BATTLE<br />FOR NYC</h3>
            <p className="tbg-sub"><strong>Tap</strong> the e-waste minions to zap them. Charge the <strong>Eco-Beam</strong> to blast the Trash-Bot — protect the city! 🌎⚡</p>
            <button className="tbg-btn" onClick={start}>▶ FIGHT</button>
          </div>
        )}
        {phase === "won" && (
          <div className="tbg-overlay tbg-win">
            <div className="tbg-burst">🌎⚡🦸</div>
            <h3 className="tbg-title">NYC IS SAVED!</h3>
            <p className="tbg-sub">Score: <strong>{finalScore}</strong> · The Eco-Allies win! 🏙️💚</p>
            <button className="tbg-btn" onClick={start}>↻ FIGHT AGAIN</button>
          </div>
        )}
        {phase === "lost" && (
          <div className="tbg-overlay tbg-lose">
            <div className="tbg-burst">🤖</div>
            <h3 className="tbg-title">THE CITY FELL…</h3>
            <p className="tbg-sub">Score: <strong>{finalScore}</strong> · Regroup, Earth Jr. — NYC needs you!</p>
            <button className="tbg-btn" onClick={start}>↻ TRY AGAIN</button>
          </div>
        )}
      </div>
    </div>
  );
};

function draw(ctx: CanvasRenderingContext2D, s: any, bg: HTMLImageElement | null, loaded: boolean) {
  const ox = s.shake ? (Math.random() - 0.5) * s.shake : 0;
  const oy = s.shake ? (Math.random() - 0.5) * s.shake : 0;
  ctx.save();
  ctx.clearRect(0, 0, W, H);
  ctx.translate(ox, oy);

  // background
  if (loaded && bg) {
    // cover-fit
    const ir = bg.width / bg.height, cr = W / H;
    let dw = W, dh = H, dx = 0, dy = 0;
    if (ir > cr) { dh = H; dw = H * ir; dx = (W - dw) / 2; } else { dw = W; dh = W / ir; dy = (H - dh) / 2; }
    ctx.drawImage(bg, dx - 6, dy - 6, dw + 12, dh + 12);
  } else {
    const grad = ctx.createLinearGradient(0, 0, 0, H);
    grad.addColorStop(0, "#16243a"); grad.addColorStop(1, "#0b3a2a");
    ctx.fillStyle = grad; ctx.fillRect(-10, -10, W + 20, H + 20);
  }
  // bottom darkening for HUD legibility
  const bgrad = ctx.createLinearGradient(0, H - 120, 0, H);
  bgrad.addColorStop(0, "rgba(5,10,16,0)"); bgrad.addColorStop(1, "rgba(5,10,16,.78)");
  ctx.fillStyle = bgrad; ctx.fillRect(0, H - 120, W, 120);

  // boss flash
  if (s.bossFlash > 0) {
    ctx.fillStyle = `rgba(34,229,143,${0.05 * s.bossFlash})`;
    ctx.fillRect(0, 0, W, 220);
  }

  // boss HP bar (over the Trash-Bot in the art)
  ctx.fillStyle = "rgba(0,0,0,.5)"; roundRect(ctx, W / 2 - 92, 14, 184, 18, 9); ctx.fill();
  ctx.fillStyle = "#e23b3b"; roundRect(ctx, W / 2 - 89, 17, 178 * (s.bossHP / 100), 12, 6); ctx.fill();
  ctx.fillStyle = "#fff"; ctx.font = "800 11px Inter, sans-serif"; ctx.textAlign = "center"; ctx.textBaseline = "middle";
  ctx.fillText("TRASH-BOT", W / 2, 23);

  // eco-beam
  if (s.beam > 0) {
    const a = s.beam / 22;
    const bg2 = ctx.createLinearGradient(0, H, 0, 60);
    bg2.addColorStop(0, `rgba(34,229,143,${0.9 * a})`);
    bg2.addColorStop(1, `rgba(62,166,255,${0.1 * a})`);
    ctx.fillStyle = bg2;
    ctx.fillRect(W / 2 - 30 * a - 14, 60, 28 + 60 * a, H - 60);
    ctx.beginPath(); ctx.fillStyle = `rgba(255,255,255,${0.5 * a})`;
    ctx.arc(W / 2, 90, 50 * a, 0, 7); ctx.fill();
  }

  // minions with red glow
  for (const m of s.minions) {
    ctx.beginPath(); ctx.fillStyle = "rgba(226,59,59,.30)"; ctx.arc(m.x, m.y, m.r + 6, 0, 7); ctx.fill();
    ctx.font = "30px serif"; ctx.textAlign = "center"; ctx.textBaseline = "middle";
    ctx.fillText(m.emoji, m.x, m.y);
  }

  // particles
  for (const p of s.parts) {
    ctx.globalAlpha = Math.max(0, p.life / 30);
    ctx.fillStyle = p.color;
    ctx.beginPath(); ctx.arc(p.x, p.y, p.size, 0, 7); ctx.fill();
    ctx.globalAlpha = 1;
  }

  // sidekick team line (defenders)
  ctx.font = "22px serif"; ctx.textAlign = "center"; ctx.textBaseline = "middle";
  for (let i = 0; i < SIDEKICKS.length; i++) {
    ctx.fillText(SIDEKICKS[i], 44 + i * ((W - 88) / 4), CITY_LINE + 18);
  }

  // eco meter
  ctx.fillStyle = "rgba(255,255,255,.18)"; roundRect(ctx, 14, H - 26, W - 28, 12, 6); ctx.fill();
  const eg = ctx.createLinearGradient(14, 0, W - 14, 0);
  eg.addColorStop(0, "#22e58f"); eg.addColorStop(1, "#3ea6ff");
  ctx.fillStyle = eg; roundRect(ctx, 14, H - 26, (W - 28) * (s.eco / 100), 12, 6); ctx.fill();
  ctx.fillStyle = "#fff"; ctx.font = "800 9px Inter, sans-serif"; ctx.textAlign = "center";
  ctx.fillText(s.eco >= 100 ? "ECO-BEAM READY!" : "ECO-BEAM", W / 2, H - 20);

  // HUD
  ctx.textAlign = "left"; ctx.fillStyle = "#fff";
  ctx.font = "800 18px 'Bangers', Inter, sans-serif";
  ctx.fillText("SCORE " + s.score, 14, H - 44);
  ctx.textAlign = "right"; ctx.font = "18px serif";
  ctx.fillText("🛡️".repeat(Math.max(0, s.shield)), W - 12, H - 42);

  // city-hit red flash
  if (s.hitFlash > 0) { ctx.fillStyle = `rgba(226,59,59,${0.05 * s.hitFlash})`; ctx.fillRect(-10, -10, W + 20, H + 20); }

  ctx.restore();
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

export default EcoBattleGame;
